import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  getAllUTMSettings,
  updateUTMSettings,
  testUTMTransformation,
  VALID_SITE_CODES,
  SITE_CODE_NAMES,
  DEFAULT_UTM_CONFIG,
  type UTMConfig,
  type SiteCode,
} from '../api/settings'
import {
  AlertCircle,
  CheckCircle2,
  Link2,
  Settings as SettingsIcon,
  TestTube,
  Save,
  X,
  Plus,
} from 'lucide-react'

export default function Settings() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  // Selected site for editing
  const [selectedSite, setSelectedSite] = useState<SiteCode>('ua')

  // Form state
  const [formData, setFormData] = useState<UTMConfig>(DEFAULT_UTM_CONFIG)
  const [newDomain, setNewDomain] = useState('')
  const [hasChanges, setHasChanges] = useState(false)

  // Test panel state
  const [showTestPanel, setShowTestPanel] = useState(false)
  const [testText, setTestText] = useState(
    'Рекомендую цей солод: [Чеський солод 50кг](https://casta-vinodelov.com/catalog/cheshskiy_solod_50kg/10133/)\n\nТакож дивіться https://casta-vinodelov.com/catalog/other/'
  )
  const [testResult, setTestResult] = useState<{ transformed: string; urls_modified: number } | null>(null)

  // Fetch all UTM settings
  const { data: allSettings, isLoading } = useQuery({
    queryKey: ['settings', 'utm', 'all'],
    queryFn: getAllUTMSettings,
  })

  // Update form when selected site changes or data loads
  useEffect(() => {
    if (allSettings?.configs && selectedSite) {
      const config = allSettings.configs[selectedSite] || DEFAULT_UTM_CONFIG
      setFormData({
        enabled: config.enabled,
        domains: [...config.domains],
        utm_source: config.utm_source,
        utm_medium: config.utm_medium,
        utm_campaign: config.utm_campaign,
        utm_content: config.utm_content,
        utm_term: config.utm_term,
      })
      setHasChanges(false)
    }
  }, [allSettings, selectedSite])

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: () => updateUTMSettings(selectedSite, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'utm'] })
      setHasChanges(false)
    },
  })

  // Test mutation
  const testMutation = useMutation({
    mutationFn: () => testUTMTransformation({ text: testText, site_code: selectedSite }),
    onSuccess: (data) => {
      setTestResult({ transformed: data.transformed, urls_modified: data.urls_modified })
    },
  })

  // Form handlers
  const updateField = <K extends keyof UTMConfig>(field: K, value: UTMConfig[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setHasChanges(true)
  }

  const addDomain = () => {
    const domain = newDomain.trim().toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/\/$/, '')

    if (domain && !formData.domains.includes(domain)) {
      updateField('domains', [...formData.domains, domain])
      setNewDomain('')
    }
  }

  const removeDomain = (domain: string) => {
    updateField('domains', formData.domains.filter(d => d !== domain))
  }

  const handleSave = () => {
    updateMutation.mutate()
  }

  const handleTest = () => {
    setTestResult(null)
    testMutation.mutate()
  }

  // Count enabled sites
  const enabledSitesCount = allSettings?.configs
    ? Object.values(allSettings.configs).filter(c => c.enabled).length
    : 0

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold text-white">{t('settings.title')}</h2>
          <p className="text-muted-200 mt-1">{t('settings.subtitle')}</p>
        </div>

        <div className="flex items-center gap-4">
          {/* Status badge */}
          <div className="px-3 py-1.5 rounded-full bg-surface-200 border border-slate-700 text-sm text-muted-100">
            {enabledSitesCount} / {VALID_SITE_CODES.length} {t('settings.sitesEnabled')}
          </div>

          {/* Test button */}
          <button
            onClick={() => setShowTestPanel(!showTestPanel)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-surface-200 border border-slate-700 text-muted-100 hover:bg-surface-300 transition-colors"
          >
            <TestTube className="h-4 w-4" />
            {t('settings.testButton')}
          </button>
        </div>
      </div>

      {/* Test Panel */}
      {showTestPanel && (
        <div className="rounded-xl border border-slate-800 bg-surface-200/80 backdrop-blur p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-100">{t('settings.testPanel.title')}</h3>
            <button
              onClick={() => setShowTestPanel(false)}
              className="text-muted-300 hover:text-muted-100"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Input */}
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-300 mb-2">
                {t('settings.testPanel.input')}
              </label>
              <textarea
                value={testText}
                onChange={(e) => setTestText(e.target.value)}
                rows={6}
                placeholder="Paste text with URLs to test..."
                className="w-full px-3 py-2 rounded-md border border-slate-700 bg-surface-300 text-slate-100 placeholder-slate-500 focus:ring-2 focus:ring-primary-500/60 focus:border-primary-400/70 transition font-mono text-sm"
              />
            </div>

            {/* Output */}
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-300 mb-2">
                {t('settings.testPanel.output')}
                {testResult && (
                  <span className="ml-2 text-primary-400">
                    ({testResult.urls_modified} URLs modified)
                  </span>
                )}
              </label>
              <textarea
                value={testResult?.transformed || ''}
                readOnly
                rows={6}
                placeholder="Result will appear here..."
                className="w-full px-3 py-2 rounded-md border border-slate-700 bg-surface-400 text-slate-300 placeholder-slate-500 font-mono text-sm"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={handleTest}
              disabled={testMutation.isPending || !testText.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500/20 border border-primary-500/50 text-primary-100 hover:bg-primary-500/30 transition-colors disabled:opacity-50"
            >
              <TestTube className="h-4 w-4" />
              {testMutation.isPending ? t('settings.testing') : t('settings.runTest')}
            </button>
          </div>
        </div>
      )}

      {/* Main Content: Site Tabs + Form */}
      <div className="grid grid-cols-12 gap-6">
        {/* Site Selector (Left) */}
        <div className="col-span-3">
          <div className="rounded-xl border border-slate-800 bg-surface-200/80 backdrop-blur p-4">
            <h3 className="text-sm font-medium text-muted-200 mb-3 px-2">{t('settings.selectSite')}</h3>
            <nav className="space-y-1">
              {VALID_SITE_CODES.map((code) => {
                const config = allSettings?.configs?.[code]
                const isEnabled = config?.enabled || false
                const isSelected = selectedSite === code

                return (
                  <button
                    key={code}
                    onClick={() => setSelectedSite(code)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                      isSelected
                        ? 'bg-primary-500/20 border border-primary-500/50 text-primary-100'
                        : 'hover:bg-surface-300 text-muted-100'
                    }`}
                  >
                    <span>{SITE_CODE_NAMES[code]}</span>
                    {isEnabled ? (
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                    ) : (
                      <span className="w-2 h-2 rounded-full bg-slate-600" />
                    )}
                  </button>
                )
              })}
            </nav>
          </div>
        </div>

        {/* Settings Form (Right) */}
        <div className="col-span-9">
          <div className="rounded-xl border border-slate-800 bg-surface-200/80 backdrop-blur p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <SettingsIcon className="h-5 w-5 text-primary-400" />
                <div>
                  <h3 className="text-lg font-semibold text-slate-100">
                    UTM {t('settings.configFor')} {SITE_CODE_NAMES[selectedSite]}
                  </h3>
                  <p className="text-sm text-muted-300">{t('settings.utmDescription')}</p>
                </div>
              </div>

              {/* Enable Toggle */}
              <label className="flex items-center gap-3 cursor-pointer">
                <span className="text-sm text-muted-200">{t('settings.enabled')}</span>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => updateField('enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                </div>
              </label>
            </div>

            {/* Form Fields */}
            <div className="space-y-6">
              {/* Domains */}
              <div>
                <label className="block text-xs uppercase tracking-wide text-muted-300 mb-2">
                  <Link2 className="inline h-3 w-3 mr-1" />
                  {t('settings.domains')}
                </label>
                <p className="text-xs text-muted-400 mb-3">{t('settings.domainsHelp')}</p>

                {/* Domain Tags */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.domains.map((domain) => (
                    <span
                      key={domain}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-surface-300 border border-slate-600 text-sm text-muted-100"
                    >
                      {domain}
                      <button
                        onClick={() => removeDomain(domain)}
                        className="text-muted-300 hover:text-red-400 ml-1"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  {formData.domains.length === 0 && (
                    <span className="text-sm text-muted-400 italic">{t('settings.noDomainsYet')}</span>
                  )}
                </div>

                {/* Add Domain Input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addDomain())}
                    placeholder="example.com"
                    className="flex-1 px-3 py-2 rounded-md border border-slate-700 bg-surface-300 text-slate-100 placeholder-slate-500 focus:ring-2 focus:ring-primary-500/60 focus:border-primary-400/70 transition"
                  />
                  <button
                    onClick={addDomain}
                    disabled={!newDomain.trim()}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-surface-300 border border-slate-600 text-muted-100 hover:bg-surface-400 transition-colors disabled:opacity-50"
                  >
                    <Plus className="h-4 w-4" />
                    {t('settings.addDomain')}
                  </button>
                </div>
              </div>

              {/* UTM Parameters Grid */}
              <div className="grid grid-cols-2 gap-4">
                {/* utm_source */}
                <div>
                  <label className="block text-xs uppercase tracking-wide text-muted-300 mb-2">
                    utm_source <span className="text-primary-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.utm_source}
                    onChange={(e) => updateField('utm_source', e.target.value)}
                    placeholder="enologic-ai"
                    className="w-full px-3 py-2 rounded-md border border-slate-700 bg-surface-300 text-slate-100 placeholder-slate-500 focus:ring-2 focus:ring-primary-500/60 focus:border-primary-400/70 transition"
                  />
                </div>

                {/* utm_medium */}
                <div>
                  <label className="block text-xs uppercase tracking-wide text-muted-300 mb-2">
                    utm_medium <span className="text-primary-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.utm_medium}
                    onChange={(e) => updateField('utm_medium', e.target.value)}
                    placeholder="answer-ai"
                    className="w-full px-3 py-2 rounded-md border border-slate-700 bg-surface-300 text-slate-100 placeholder-slate-500 focus:ring-2 focus:ring-primary-500/60 focus:border-primary-400/70 transition"
                  />
                </div>

                {/* utm_campaign */}
                <div>
                  <label className="block text-xs uppercase tracking-wide text-muted-300 mb-2">
                    utm_campaign <span className="text-muted-400 text-xs normal-case">({t('settings.optional')})</span>
                  </label>
                  <input
                    type="text"
                    value={formData.utm_campaign}
                    onChange={(e) => updateField('utm_campaign', e.target.value)}
                    placeholder=""
                    className="w-full px-3 py-2 rounded-md border border-slate-700 bg-surface-300 text-slate-100 placeholder-slate-500 focus:ring-2 focus:ring-primary-500/60 focus:border-primary-400/70 transition"
                  />
                </div>

                {/* utm_content */}
                <div>
                  <label className="block text-xs uppercase tracking-wide text-muted-300 mb-2">
                    utm_content <span className="text-muted-400 text-xs normal-case">({t('settings.optional')})</span>
                  </label>
                  <input
                    type="text"
                    value={formData.utm_content}
                    onChange={(e) => updateField('utm_content', e.target.value)}
                    placeholder=""
                    className="w-full px-3 py-2 rounded-md border border-slate-700 bg-surface-300 text-slate-100 placeholder-slate-500 focus:ring-2 focus:ring-primary-500/60 focus:border-primary-400/70 transition"
                  />
                </div>

                {/* utm_term */}
                <div className="col-span-2">
                  <label className="block text-xs uppercase tracking-wide text-muted-300 mb-2">
                    utm_term <span className="text-muted-400 text-xs normal-case">({t('settings.optional')})</span>
                  </label>
                  <input
                    type="text"
                    value={formData.utm_term}
                    onChange={(e) => updateField('utm_term', e.target.value)}
                    placeholder=""
                    className="w-full px-3 py-2 rounded-md border border-slate-700 bg-surface-300 text-slate-100 placeholder-slate-500 focus:ring-2 focus:ring-primary-500/60 focus:border-primary-400/70 transition"
                  />
                </div>
              </div>
            </div>

            {/* Footer with Save Button */}
            <div className="mt-6 pt-4 border-t border-slate-700 flex items-center justify-between">
              {/* Status Messages */}
              <div>
                {updateMutation.isSuccess && (
                  <div className="flex items-center gap-2 text-green-400 text-sm">
                    <CheckCircle2 className="h-4 w-4" />
                    {t('settings.saved')}
                  </div>
                )}
                {updateMutation.isError && (
                  <div className="flex items-center gap-2 text-red-400 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    {(updateMutation.error as Error)?.message || t('settings.saveFailed')}
                  </div>
                )}
                {hasChanges && !updateMutation.isPending && !updateMutation.isSuccess && (
                  <div className="text-amber-400 text-sm">
                    {t('settings.unsavedChanges')}
                  </div>
                )}
              </div>

              {/* Save Button */}
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending || !hasChanges}
                className="inline-flex items-center gap-2 px-6 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="h-4 w-4" />
                {updateMutation.isPending ? t('settings.saving') : t('settings.save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
